/* eslint-disable i18next/no-literal-string */
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { AddToCartButton } from "~/components/landing/Pricing";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const carSunsetLotPortrait = imageUrl("/images/landing/car-sunset-lot-portrait.jpg");
const carSunsetLotLandscape = imageUrl("/images/landing/car-sunset-lot-landscape.jpg");

/**
 * Ported from bouncieweb's `LandingPageComponents/CartAndPricing.js`.
 *
 * A full-bleed black band with a dimmed car photo behind a large headline and
 * two CTAs. The portrait crop shows below sm, the landscape crop from sm up.
 * Rounds its corners and gains heavy vertical padding at xlg.
 *
 * Deviation (documented): the "View Pricing" button used `next/router` to push
 * `/pricing`. That path is not a route in this app (it isn't a page in
 * bouncieweb either — it resolves at the bouncie.com edge), so this port links
 * to the absolute production URL rather than a route that would 404 locally.
 * Same rationale as `Compatibility`'s `https://www.bouncie.com/api` calls.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:
 *   lg 1024px -> lg:  xlg 1280px -> xl:
 */

const PRICING_URL = "https://www.bouncie.com/pricing";

export default function CartAndPricing() {
  return (
    // Wrapper
    <div className="xl:flex xl:items-center xl:justify-center xl:px-4">
      {/* Section */}
      <div
        className={cn(
          "relative mb-20 w-full max-w-[1450px] overflow-hidden py-8",
          "sm:py-24 xl:rounded-[20px] xl:py-40",
        )}
        style={{ background: colors.black }}
      >
        {/* CarPortrait — hidden from sm up */}
        <Picture
          src={carSunsetLotPortrait}
          alt="A car pulling away from a parking lot"
          className={cn(
            "absolute top-0 left-0 h-full w-full max-w-none object-cover object-top",
            "opacity-80 md:opacity-90",
            "sm:hidden",
          )}
        />
        {/* CarLandscape — hidden below sm */}
        <Picture
          src={carSunsetLotLandscape}
          alt="A car pulling away from a parking lot"
          className={cn(
            "absolute top-0 left-0 hidden h-full w-full max-w-none object-cover object-top",
            "opacity-80 md:opacity-90",
            "sm:block",
          )}
        />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy-wide">
          {/* ContentWrapper */}
          <div
            className="relative flex flex-col gap-44 [text-wrap:wrap] sm:gap-8"
            style={{ textShadow: "rgba(0,0,0,1) 0px 0 2rem" }}
          >
            {/* CustomBentoGridH2Large */}
            <h2
              className={cn(
                "font-display relative max-w-[550px] pr-8 leading-[1.15em] font-extrabold text-white",
                "text-[10.7vw] min-[431px]:text-[40px] lg:text-[48px] xl:max-w-[650px]",
              )}
            >
              Wherever they go, Bouncie keeps you connected.
            </h2>

            {/* ButtonsWrapper */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <AddToCartButton data-sid="landing-cart-pricing-add-to-cart" className="trigger-gtm-pricing-add-to-cart w-full p-4 font-bold sm:w-auto md:w-auto" />
              {/* CustomBottomPricing */}
              <a data-sid="landing-bottom-pricing-link"
                href={PRICING_URL}
                className={cn(
                  "trigger-gtm-turo-landing-bottom-pricing",
                  "inline-flex cursor-pointer justify-center rounded-[10px] border-0 p-4",
                  "text-[18px] font-bold hover:no-underline",
                  "bg-white hover:bg-[#F2F2F2]",
                )}
                style={{ color: colors.blue }}
              >
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
